import { Button, IconButton, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { athleteDisplayName, TABLET_ONLY_PROFILE_ROLE } from '@strength/domain';
import { useRouter } from '@tanstack/react-router';
import { MdCheck, MdClose, MdLock, MdLogout } from '@teamworksdev/icons';
import { DateTime } from 'luxon';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogout } from '../../../../auth';
import { setTabletTeamIdsHeader } from '../../../../trpc/common';
import { trpc } from '../../../../trpc/react';
import { SearchInput } from '../../../components/input/search-input';
import { TeamSelect } from '../../../components/layout/TeamSelect';
import { useViewer } from '../../../context/viewer';
import { useBootstrap } from '../../../hooks/useBootstrap';
import { useStorage } from '../../../hooks/useStorage';
import { TabletExitWarning } from './components/dialogs/TabletExitWarning';
import { TabletLockWarning } from './components/dialogs/TabletLockWarning';
import { TabletProvider } from './context/TabletContext';
import { TabletActualsCollection } from './TabletActualsCollection';
import { Athlete, AthleteWithTeam, TabletScreen } from './types';

export const TabletActualsPage = () => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const logout = useLogout();
  const [athleteSearchTerm, setAthleteSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [exitWarningOpen, setExitWarningOpen] = useState(false);
  const [lockWarningOpen, setLockWarningOpen] = useState(false);
  const {
    team: { selectedTeam },
    profile: { role: profileRole },
  } = useViewer();
  const [teamId, setTeamId] = useStorage<string>('tabletTeamId', selectedTeam);

  // On mount, set the teamId to the selected team
  // don't add setTeamId to the dependency array because it will keep teamId as the selectedTeam value
  useEffect(() => {
    if (selectedTeam) {
      setTeamId(selectedTeam);
    }
  }, [selectedTeam]);

  const [currentScreen, setCurrentScreen] = useStorage<TabletScreen>(
    'tabletCurrentScreen',
    'selection',
  );
  const [selectedAthletes, setSelectedAthletes] = useStorage<AthleteWithTeam[]>(
    'tabletSelectedAthletes',
    [],
  );

  // Sorted unique team IDs of the currently-selected athletes (collection scope).
  // Sorted so the queryKeyHashFn produces the same hash regardless of athlete
  // selection order ([teamA, teamB] ≡ [teamB, teamA]).
  const selectedTeamIds = useMemo(
    () => [...new Set(selectedAthletes.map((a) => a.teamId))].sort(),
    [selectedAthletes],
  );

  // Keep the tw-team-ids header in sync synchronously during render so
  // queryKeyHashFn (which calls getTeamIdsHeader() at render time) always sees
  // the correct scope. Updating in useLayoutEffect would be too late — the hash
  // would be computed with the previous header, causing a scope mismatch.
  if (currentScreen === 'collection') {
    setTabletTeamIdsHeader(selectedTeamIds);
  } else {
    setTabletTeamIdsHeader(teamId ? [teamId] : []);
  }

  // Clear the header when the component unmounts.
  useLayoutEffect(() => {
    return () => setTabletTeamIdsHeader([]);
  }, []);

  const [storedIsTabletLocked, setIsTabletLocked] = useStorage(
    'isTabletLocked',
    false,
  );

  // Tablet-only role users are always locked, regardless of stored value
  // Need this since useStorage only applied default value if no value is stored in localStorage
  const isTabletLocked =
    profileRole === TABLET_ONLY_PROFILE_ROLE || storedIsTabletLocked;

  // The tablet bootstrap is bounded to a window anchored at "today"
  // (SC-5518) — the group carries the anchor date so the payload stays the
  // size the collection screen renders. Device-local plain date, like
  // mobile's bucket queries; the timezone shapes the window's timestamp
  // boundaries (BulkActuals convention).
  //
  // The kiosk stays mounted for days at a time, and an idle page never
  // re-renders, so the anchor is state that refreshes at local midnight —
  // otherwise a stale anchor keeps a stale window (and stale
  // categorizeWorkoutsByDate buckets, which only run on render).
  const [bootstrapDate, setBootstrapDate] = useState(() =>
    DateTime.local().toISODate(),
  );
  const timezone = DateTime.local().zoneName;

  useEffect(() => {
    const now = DateTime.local();
    // +1s past midnight: setTimeout truncates the fractional-ms delay, so a
    // timer aimed exactly at midnight can fire a hair early, read the same
    // date, no-op the state change, and never re-arm (stale anchor forever).
    const nextMidnight = now.plus({ days: 1 }).startOf('day').plus({
      seconds: 1,
    });
    const timeout = window.setTimeout(
      () => setBootstrapDate(DateTime.local().toISODate()),
      Math.max(0, nextMidnight.diff(now).as('milliseconds')),
    );

    return () => window.clearTimeout(timeout);
  }, [bootstrapDate]);

  const syncGroups = useMemo(() => {
    if (!teamId || selectedAthletes.length === 0) {
      return [];
    }
    return selectedAthletes.map((athlete) => ({
      type: 'TeamCoach_TabletAthlete' as const,
      teamId: athlete.teamId,
      athleteId: athlete.id,
      date: bootstrapDate,
      timezone,
    }));
  }, [teamId, selectedAthletes, bootstrapDate, timezone]);

  const { isLoading } = useBootstrap(syncGroups);

  // Create debounced function using MUI's debounce utility
  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTerm(value);
      }, 300),
    [],
  );

  // teamsPreview/teamAthletes are single-team roster reads only used on the
  // selection screen. Gate them to that screen so they don't re-key/refetch
  // under the multi-team `tw-team-ids` header the collection screen sets.
  const selectionQueriesEnabled = currentScreen === 'selection';

  // Get available teams and set default team on success
  const teamsResult = trpc.roster.teamsPreview.useQuery(undefined, {
    enabled: selectionQueriesEnabled,
    onSuccess: (teams) => {
      if (teams && teams.length > 0 && !teamId) {
        // No team selected, use first team as default
        const firstTeam = teams[0];
        if (firstTeam) {
          setTeamId(firstTeam.id);
        }
      }
    },
  });

  const teamAthleteResult = trpc.roster.teamAthletes.useQuery(
    {},
    {
      enabled: selectionQueriesEnabled,
      staleTime: 10_000,
    },
  );
  const teamAthletes = useMemo(
    () => teamAthleteResult.data || [],
    [teamAthleteResult.data],
  );

  // Helper function to get team name by team ID
  const getTeamName = (teamId: string) => {
    const teams = teamsResult.data || [];
    const team = teams.find((t) => t.id === teamId);
    return team?.name || 'Unknown Team';
  };

  // Filter athletes based on search term
  const filteredAthletes = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return teamAthletes;
    }

    return teamAthletes.filter((athlete) =>
      athleteDisplayName(athlete)
        .toLowerCase()
        .includes(debouncedSearchTerm.toLowerCase()),
    );
  }, [teamAthletes, debouncedSearchTerm]);

  const selectAthlete = (athlete: Athlete) => {
    const athleteWithTeam = {
      ...athlete,
      teamName: getTeamName(teamId),
      teamId: teamId,
    };
    setSelectedAthletes([
      ...selectedAthletes.filter(
        (selectedAthlete) => selectedAthlete.id !== athlete.id,
      ),
      athleteWithTeam,
    ]);
  };

  const deselectAthlete = (athleteId: string) => {
    setSelectedAthletes(
      selectedAthletes.filter((athlete) => athlete.id !== athleteId),
    );
  };

  const handleExitTabletMode = () => {
    setExitWarningOpen(false);
    // Reset tablet state
    resetSelections();
    navigate({
      to: '/actuals',
    });
  };

  const exitTabletWorkflow = () => {
    setCurrentScreen('selection');
  };

  const resetSelections = () => {
    setTeamId('');
    setSelectedAthletes([]);
    setAthleteSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentScreen('selection');
  };

  const handleLockTabletMode = () => {
    setIsTabletLocked(true);
    setLockWarningOpen(false);
  };

  const handleUnlockTabletMode = () => {
    setIsTabletLocked(false);
  };

  return (
    <>
      {currentScreen === 'selection' && (
        <div className="flex flex-col justify-between p-8 bg-gray-100 w-screen h-full overflow-hidden">
          <div className="flex flex-col grow overflow-hidden">
            <div className="flex flex-row justify-between items-center">
              {isTabletLocked ? (
                <Button
                  onClick={async () => {
                    handleUnlockTabletMode();
                    await logout();
                  }}
                  variant="text"
                  className="flex flex-row items-center gap-2"
                >
                  <MdLogout size={20} />
                  {t('logOut')}
                </Button>
              ) : (
                <Button
                  onClick={() => setExitWarningOpen(true)}
                  sx={{ p: 0 }}
                  variant="text"
                  className="justify-start flex-shrink-0"
                >
                  {t('exitTabletMode')}
                </Button>
              )}
              {!isTabletLocked && (
                <div
                  className="flex flex-row items-center gap-2 hover:cursor-pointer"
                  onClick={() => setLockWarningOpen(true)}
                >
                  <MdLock />
                  <Typography variant="body2Medium">
                    {t('lockTablet')}
                  </Typography>
                </div>
              )}
            </div>
            <div className="mt-4 flex">
              <Typography variant="h2">{t('selectAthletes')}</Typography>
            </div>
            <div className="flex flex-row gap-4 grow overflow-hidden">
              <div className="mt-4 flex flex-col gap-4 w-2/5">
                <div className="flex flex-row justify-center gap-4">
                  <TeamSelect
                    teamId={teamId}
                    onSelect={(newTeamId) => {
                      if (newTeamId === teamId) {
                        return;
                      }
                      setTabletTeamIdsHeader(newTeamId ? [newTeamId] : []);
                      setTeamId(newTeamId);
                    }}
                    sx={{ width: '100%', mt: 0 }}
                  />
                  <SearchInput
                    value={athleteSearchTerm}
                    onChange={(e: string) => {
                      setAthleteSearchTerm(e);
                      debouncedSetSearchTerm(e);
                    }}
                    placeholder={t('search')}
                    showLabel={false}
                    sx={{ width: '100%', mt: 0 }}
                  />
                </div>
                <div className="border-2 border-grey-200 rounded-md px-4 py-2 flex flex-col flex-1 h-full overflow-y-auto">
                  {filteredAthletes?.map((athlete) => {
                    const athleteId = athlete.id;
                    const isSelected = selectedAthletes.some(
                      (selectedAthlete) => selectedAthlete.id === athleteId,
                    );

                    return (
                      <div
                        key={athleteId}
                        className="flex flex-row justify-between items-center bg-white border-2 rounded-md p-3 my-1 h-[44px]"
                        onClick={() => {
                          if (isSelected) {
                            deselectAthlete(athleteId);
                          } else {
                            selectAthlete(athlete);
                          }
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {athleteDisplayName(athlete)}
                        </Typography>
                        {isSelected ? <MdCheck size={20} /> : null}
                      </div>
                    );
                  })}
                  {filteredAthletes?.length === 0 &&
                    teamAthletes?.length > 0 && (
                      <Typography className="text-center text-gray-500 mt-4">
                        {t('noAthletesFoundMatching', {
                          searchTerm: debouncedSearchTerm,
                        })}
                      </Typography>
                    )}
                  {teamAthletes?.length === 0 && (
                    <Typography className="text-center text-gray-500 mt-4">
                      {t('noAthletesAvailableForTeam')}
                    </Typography>
                  )}
                </div>
              </div>
              <div className="border-2 border-grey-200 rounded-md w-3/5 px-4 py-2 flex flex-col h-full overflow-y-auto">
                {selectedAthletes.map((athlete) => {
                  return (
                    <div
                      key={athlete.id}
                      className="flex w-full flex-row justify-between items-center bg-white border-2 rounded-md p-3 my-1 h-[44px]"
                      onClick={() => {
                        deselectAthlete(athlete.id);
                      }}
                    >
                      <div className="flex flex-col">
                        <Typography variant="body2" fontWeight={600}>
                          {athleteDisplayName(athlete)}
                        </Typography>
                      </div>
                      <div className="flex flex-row items-center gap-2">
                        <Typography
                          variant="subtitle1"
                          className="text-gray-500"
                        >
                          {athlete.teamName}
                        </Typography>
                        <IconButton onClick={() => deselectAthlete(athlete.id)}>
                          <MdClose size={20} />
                        </IconButton>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex gap-2 self-end mt-4">
            <Button
              variant="outlined"
              className="bg-white"
              onClick={() => resetSelections()}
            >
              {t('resetSelections')}
            </Button>
            <Button
              disabled={selectedAthletes.length === 0}
              onClick={() => {
                setCurrentScreen('collection');
              }}
            >
              {t('continue')}
            </Button>
          </div>
          <TabletExitWarning
            open={exitWarningOpen}
            setOpen={setExitWarningOpen}
            handleExitTabletMode={handleExitTabletMode}
          />
          <TabletLockWarning
            open={lockWarningOpen}
            setOpen={setLockWarningOpen}
            handleLockTabletMode={handleLockTabletMode}
          />
        </div>
      )}
      {currentScreen === 'collection' && isLoading && (
        <div className="flex flex-col items-center justify-center h-full">
          <Typography variant="body1" className="mt-4">
            {t('loading')}
          </Typography>
        </div>
      )}
      {currentScreen === 'collection' && !isLoading && (
        <TabletProvider
          selectedAthletes={selectedAthletes}
          deselectAthlete={deselectAthlete}
          onExit={() => exitTabletWorkflow()}
        >
          <TabletActualsCollection />
        </TabletProvider>
      )}
    </>
  );
};
